// External Packages
import { useState, useContext, useEffect, Fragment } from "react";
import { useLocation } from "react-router-dom";
import {
  MdAdd,
  MdAddTask,
  MdBarChart,
  MdClose,
  MdRefresh,
} from "react-icons/md";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import SearchBar from "../UI/SearchBar";
import PaymentCard from "./PaymentCard";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import DateFilter from "../UI/DateFilter";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import ListLabels from "../UI/ListLabels";
import NoRecordsFound from "../UI/NoRecordsFound";
import InvoiceModal from "./InvoiceModal";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";
import Modal from "../UI/Modal";
import SelectPair from "../UI/SelectPair";
import DetailPair from "../UI/DetailPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { format } from "date-fns";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase, truncate } from "../../util/format-text";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";

/*
  * All Transfers Component
  This page displays all payments in the database.
*/

export default function Transfers({refresh}) {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    to: "",
    from: "",
    limit: "50",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [transfers, setTransfers] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [add, setAdd] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      let filters = [];
      if (filter.to) filters.push(`to: {eq: "${filter.to}"}`);
      if (filter.from) filters.push(`from: {eq: "${filter.from}"}`);
      if (filter.sd && filter.ed)
        filters.push(
          `dateCreated: {ge: "${filter.sd}"}`,
          `dateCreated: {le: "${filter.ed}"}`
        );
      const data = await requestAdminAPI("filter", {
        table: "transfer",
        limit: filter.limit,
        filters: filters,
        columns: ["createdBy { name }"],
      });

      // Replace URL
      let url = "/payments/report?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });
      setTransfers(data);
      refresh && refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "From",
      options: [
        { label: "All", value: "" },
        { label: "Cash", value: "_cash" },
        { label: "Chase", value: "_zelle" },
        { label: "PNC", value: "_clover" },
      ],
      selected: filter.from,
    },
    {
      label: "To",
      options: [
        { label: "All", value: "" },
        { label: "Cash", value: "_cash" },
        { label: "Chase", value: "_zelle" },
        { label: "PNC", value: "_clover" },
      ],
      selected: filter.to,
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="grid gap-4">
      <AddTransfer open={add} setOpen={setAdd} onComplete={fetchRecords} />
      <Heading title="Transfers" isBackArrow={false}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdAdd
          onClick={() => setAdd(true)}
          className="text-3xl icon"
          title="Add Transfer"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl icon"
          title="Refresh"
        />
        <ExportCsvButton array={transfers} filename="transfers" />
      </Heading>

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : transfers.length > 0 ? (
        <div className="gap-4 list">
          <div className="grid-cols-7 labels">
            <ListLabels
              labels={["Date", "From - To", {name: "Details", classes: "lg:col-span-2"}, "Logged By", "Receipt", "Amount"]}
            />
          </div>
          {transfers.map((transfer, idx) => (
            <div key={idx} className="grid grid-cols-7 cursor-default pill">
              <p className="text-stone-400">
                {isoDateToString(transfer.dateCreated)}
              </p>
              <p className="text-stone-200">
                {toTitleCase(transfer.from.replace("_", ""))} to {transfer.to == "_zelle"
                  ? "Chase"
                  : transfer.to == "_clover"
                  ? "PNC"
                  : toTitleCase(transfer.to.replace("_", ""))}
              </p>
              <p className="text-stone-200 lg:col-span-2">
                {transfer.reason ? truncate(transfer.reason) : "-"}
              </p>
              <p className="text-stone-400">{transfer.createdBy?.name}</p>
              <a
                href={transfer.receipt || ""}
                target="_blank"
                className="text-yellow-600 cursor-pointer"
              >
                {transfer.receipt ? "Link" : "-"}
              </a>
              <p className="font-semibold">${transfer.amount.toFixed(2)}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={transfers.length}
      ></PaginationMenu>
    </div>
  );
}

/** ========================================================================
 * * ADD TRANSFER
 * ========================================================================== */

const AddTransfer = ({ open, setOpen, onComplete }) => {
  // Initialize States
  const [loading, setLoading] = useState(false);
  const { setWarning } = useContext(UtilityContext);

  /* ======================= ADD TRANSFER ======================= */
  const submitHandler = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      const from = document.getElementById("from").value;
      const to = document.getElementById("to").value;
      const amount = document.getElementById("amount").value;
      const details = document.getElementById("details").value;
      
      if (!from || !to || !amount) {
        throw new Error("Please fill in all required fields (From, To, and Amount).");
      }
      
      const payload = {
        from,
        to,
        amount,
        details,
      };
      
      // Upload receipt if provided
      const receipt = document.getElementById("receipt").files[0];
      if (receipt) {
        try {
          payload.receipt = await uploadFile(
            `transfers/${Date.now()}`,
            receipt,
            () => {}
          );
        } catch (uploadError) {
          // Handle Firebase Storage upload errors
          if (uploadError.message && uploadError.message.includes("Permission denied")) {
            throw new Error("Failed to upload receipt: Permission denied. Please check Firebase Storage permissions or contact support.");
          }
          throw new Error(`Failed to upload receipt: ${uploadError.message || "Unknown error"}`);
        }
      }
      
      await requestAdminAPI("add-transfer", payload);
      onComplete();
      setLoading(false);
      setOpen(false);
    } catch (err) {
      console.error(err);
      // Error message will be displayed via requestAdminAPI's setWarning for API errors
      // But we also need to handle errors that occur before the API call (validation, upload)
      if (err.message && !err.message.includes("You are not authorized")) {
        setWarning(err.message);
      }
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[40vw]">
        <Heading
          title="Add Transfer"
          text="Use this form to log cash deposits."
          isBackArrow={false}
        >
          <MdClose className="text-2xl icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="info">
          <SelectPair
            label="From"
            options={[
              { name: "Cash", id: "_cash" },
              { name: "Chase", id: "_zelle" },
              { name: "PNC", id: "_clover" },
            ]}
            edit={true}
            inputOptions={{ id: "from", defaultValue: "_cash" }}
          />
          <SelectPair
            label="To"
            options={[
              { name: "Cash", id: "_cash" },
              { name: "Chase", id: "_zelle" },
              { name: "PNC", id: "_clover" },
            ]}
            edit={true}
            inputOptions={{ id: "to", defaultValue: "_zelle" }}
          />
          <DetailPair
            label="Amount"
            edit={true}
            inputOptions={{
              id: "amount",
              type: "number",
              step: "0.01",
              required: true,
              min: 0,
            }}
          />
          <LinkPair
            label="Receipt"
            edit={true}
            inputOptions={{
              id: "receipt",
              type: "file",
            }}
          />
          <div className="lg:col-span-4">
            <label className="text-sm text-stone-400">Details:</label>
            <textarea
              id="details"
              className="w-full border input border-stone-600"
              rows="3"
              placeholder="If a deposit was made by someone else, please write it here..."
              required
            />
          </div>
          <div className="mt-4 lg:col-span-4">
            {loading ? (
              <LoadingSpinnerWrapper />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="btn-secondary" onClick={() => setOpen(false)}>
                  Cancel
                </div>
                <div onClick={submitHandler} className="self-end btn-primary">
                  Confirm
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
